/**
 * In-memory bookings store. Persists to sessionStorage so a page refresh keeps a
 * confirmed booking retrievable. Swap this for real POST/GET calls when integrating.
 */

const STORAGE_KEY = "falcon_bookings";

const load = () => {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
};

const save = (store) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    /* ignore quota / privacy-mode errors */
  }
};

const randDigits = (n) =>
  Array.from({ length: n }, () => Math.floor(Math.random() * 10)).join("");

const randAlpha = (n) => {
  const letters = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  return Array.from({ length: n }, () => letters[Math.floor(Math.random() * letters.length)]).join("");
};

/** Generate a booking reference like "FALC-8F3K2Q". */
export const generateBookingReference = () => `FALC-${randAlpha(2)}${randDigits(2)}${randAlpha(2)}`;

/** Generate a 6-char alphanumeric PNR like "X7K2QP". */
export const generatePNR = () => `${randAlpha(3)}${randDigits(1)}${randAlpha(2)}`;

/**
 * Create and persist a booking record.
 * @param {import('../types').BookingPayload} payload
 * @returns {import('../types').Booking}
 */
export function createBookingRecord(payload) {
  const store = load();
  const reference = generateBookingReference();
  const booking = {
    reference,
    pnr: generatePNR(),
    status: "CONFIRMED",
    ...payload,
  };
  store[reference] = booking;
  save(store);
  return booking;
}

/**
 * Look up a booking by reference.
 * @param {string} reference
 * @returns {import('../types').Booking | null}
 */
export function getBookingRecord(reference) {
  const store = load();
  return store[reference] || null;
}

const bookingsStore = { createBookingRecord, getBookingRecord, generateBookingReference, generatePNR };
export default bookingsStore;
